import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { InvoicePdfInput } from "../features/billing/invoices/invoicePdf";
import {
  PortalFile,
  PortalFileLists,
  PortalInviteInfo,
  PortalInvoice,
  PortalMe,
  PortalSession,
} from "../features/portal/portal.types";
import { getPortalSession } from "../features/portal/portalSession";
import {
  SignatureLinkInfo,
  SignatureRequestView,
  SignPayload,
} from "../features/signatures/signature.types";

/** The client portal's API (clients, not firm staff), with the portal's own token. */
export const portalSlice = createApi({
  reducerPath: "portalSlice",
  tagTypes: ["PortalMe", "PortalFiles", "PortalSignatures", "PortalInvoices"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const session = getPortalSession();
      if (session) headers.set("Authorization", `Bearer ${session.token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    portalPasswordSignIn: build.mutation<
      PortalSession,
      { email: string; password: string }
    >({
      query: (body) => ({
        url: "api/portal/sign-in/password",
        method: "POST",
        body,
      }),
    }),
    portalSendCode: build.mutation<boolean, { email: string }>({
      query: (body) => ({
        url: "api/portal/sign-in/send-code",
        method: "POST",
        body,
      }),
    }),
    portalCodeSignIn: build.mutation<
      PortalSession,
      { email: string; code: string }
    >({
      query: (body) => ({
        url: "api/portal/sign-in/code",
        method: "POST",
        body,
      }),
    }),
    getPortalInvite: build.query<PortalInviteInfo, string>({
      query: (token) => `api/portal/invites/${encodeURIComponent(token)}`,
    }),
    acceptPortalInvite: build.mutation<
      PortalSession,
      { token: string; password?: string }
    >({
      query: ({ token, password }) => ({
        url: `api/portal/invites/${encodeURIComponent(token)}/accept`,
        method: "POST",
        body: { password: password || null },
      }),
    }),
    getPortalMe: build.query<PortalMe, void>({
      query: () => "api/portal/me",
      providesTags: ["PortalMe"],
    }),
    setPortalPassword: build.mutation<boolean, { password: string }>({
      query: (body) => ({ url: "api/portal/me/password", method: "PUT", body }),
      invalidatesTags: ["PortalMe"],
    }),
    getPortalFiles: build.query<PortalFileLists, string>({
      query: (customerId) => `api/portal/clients/${customerId}/files`,
      providesTags: ["PortalFiles"],
    }),
    uploadPortalFile: build.mutation<
      PortalFile,
      { customerId: string; file: File; note?: string }
    >({
      query: ({ customerId, file, note }) => {
        const body = new FormData();
        body.append("file", file);
        if (note) body.append("note", note);
        return {
          url: `api/portal/clients/${customerId}/files`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["PortalFiles"],
    }),
    deletePortalFile: build.mutation<boolean, string>({
      query: (documentId) => ({
        url: `api/portal/files/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PortalFiles"],
    }),
    getPortalDownloadUrl: build.mutation<{ url: string }, string>({
      query: (documentId) => ({
        url: `api/portal/files/${documentId}/download`,
        method: "GET",
      }),
    }),
    getPortalInvoices: build.query<PortalInvoice[], string>({
      query: (customerId) => `api/portal/clients/${customerId}/invoices`,
      providesTags: ["PortalInvoices"],
    }),
    getPortalInvoicePdfData: build.mutation<
      Omit<InvoicePdfInput, "fileTitle" | "logoUrl"> & {
        logoDataUrl?: string | null;
        clientName: string;
      },
      { customerId: string; invoiceId: string }
    >({
      query: ({ customerId, invoiceId }) => ({
        url: `api/portal/clients/${customerId}/invoices/${invoiceId}/pdf-data`,
        method: "GET",
      }),
    }),
    getSignatureLink: build.query<SignatureLinkInfo, string>({
      query: (token) =>
        `api/portal/signatures/link/${encodeURIComponent(token)}`,
    }),
    getMySignatures: build.query<SignatureRequestView[], void>({
      query: () => "api/portal/signatures",
      providesTags: ["PortalSignatures"],
    }),
    getPortalSigningView: build.query<SignatureRequestView, string>({
      query: (id) => `api/portal/signatures/${id}`,
    }),
    portalSign: build.mutation<
      SignatureRequestView,
      { id: string; body: SignPayload }
    >({
      query: ({ id, body }) => ({
        url: `api/portal/signatures/${id}/sign`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PortalSignatures", "PortalFiles"],
    }),
    portalDecline: build.mutation<boolean, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `api/portal/signatures/${id}/decline`,
        method: "POST",
        body: { reason: reason || null },
      }),
      invalidatesTags: ["PortalSignatures"],
    }),
  }),
});

export const {
  usePortalPasswordSignInMutation,
  usePortalSendCodeMutation,
  usePortalCodeSignInMutation,
  useGetPortalInviteQuery,
  useAcceptPortalInviteMutation,
  useGetPortalMeQuery,
  useSetPortalPasswordMutation,
  useGetPortalFilesQuery,
  useUploadPortalFileMutation,
  useGetPortalDownloadUrlMutation,
  useDeletePortalFileMutation,
  useGetPortalInvoicesQuery,
  useGetPortalInvoicePdfDataMutation,
  useGetSignatureLinkQuery,
  useGetMySignaturesQuery,
  useGetPortalSigningViewQuery,
  usePortalSignMutation,
  usePortalDeclineMutation,
} = portalSlice;
